import {
    addDoc,
    arrayUnion,
    collectionGroup,
    collection,
    doc,
    getFirestore,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where,
    getDocs,
} from './instrumentedFirestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './app';
import { COLLECTIONS } from '@constants/firestorePaths';
import { ENV } from '@configs/env';
import { isDirectoryTeacher } from './teacherService';

function db() {
    return getFirestore(getFirebaseApp());
}

const STUDENT_SUBCOLLECTION = 'student';
const TEACHER_SUBCOLLECTION = 'teacher';
const STAFF_SUBCOLLECTION = 'staff';
const NOTICE_SUBCOLLECTION = 'notice';
const ANNOUNCEMENT_SUBCOLLECTION = 'announcement';
const ATTANDANCE_SUBCOLLECTION = 'attandance';

function roleRef(tenantId, role) {
    if (!tenantId) throw new Error('Missing tenantId');
    const sub =
        role === 'student' ? STUDENT_SUBCOLLECTION :
            role === 'teacher' ? TEACHER_SUBCOLLECTION :
                STAFF_SUBCOLLECTION;
    return collection(db(), COLLECTIONS.NOTIFICATIONS, tenantId, sub);
}

function noticeUserItemsRef(tenantId, type, userUid) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!userUid) throw new Error('Missing userUid');
    const kind = type === 'notice' ? NOTICE_SUBCOLLECTION : ANNOUNCEMENT_SUBCOLLECTION;
    return collection(db(), COLLECTIONS.NOTIFICATIONS, tenantId, kind, userUid, 'items');
}

function attandanceUserItemsRef(tenantId, userUid) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!userUid) throw new Error('Missing userUid');
    return collection(db(), COLLECTIONS.NOTIFICATIONS, tenantId, ATTANDANCE_SUBCOLLECTION, userUid, 'items');
}

function notificationSummaryRef(tenantId) {
    if (!tenantId) throw new Error('Missing tenantId');
    return doc(db(), COLLECTIONS.NOTIFICATIONS, tenantId);
}

function buildNotificationSummary(tenantId, rows) {
    const summary = {
        tenantId,
        totalNotifications: 0,
        totalAnnouncementNotifications: 0,
        totalNoticeNotifications: 0,
        totalAttandanceNotifications: 0,
        totalRecipients: 0,
        totalReadEvents: 0,
        totalUnreadRecipients: 0,
        byType: { announcement: 0, notice: 0, attandance: 0 },
    };
    (rows || []).forEach((row) => {
        const type = row?.type === 'notice' ? 'notice' : row?.type === 'attandance' ? 'attandance' : 'announcement';
        const recipientCount = Number(row?.recipientCount || (row?.recipientIds || []).length || 0);
        const readCount = Array.isArray(row?.readBy) ? row.readBy.length : 0;
        const unreadCount = Math.max(0, recipientCount - readCount);
        summary.totalNotifications += 1;
        summary.totalRecipients += recipientCount;
        summary.totalReadEvents += readCount;
        summary.totalUnreadRecipients += unreadCount;
        summary.byType[type] += 1;
        if (type === 'notice') summary.totalNoticeNotifications += 1;
        else if (type === 'attandance') summary.totalAttandanceNotifications += 1;
        else summary.totalAnnouncementNotifications += 1;
    });
    return summary;
}

async function refreshNotificationSummary(tenantId) {
    const itemsCg = collectionGroup(db(), 'items');
    const snap = await getDocs(
        query(
            itemsCg,
            where('tenantId', '==', tenantId),
            where('type', 'in', ['notice', 'announcement', 'attandance'])
        )
    );
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const summary = buildNotificationSummary(tenantId, rows);
    await setDoc(
        notificationSummaryRef(tenantId),
        {
            ...summary,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
    return summary;
}

function normalizeRecipientUid(row) {
    return row?.authUid || row?.id || row?.uid || null;
}

function normalizeAudienceRows(rows, role) {
    return (rows || []).map((row) => {
        const id = row.id || row.authUid || row.uid;
        const label = row.displayName || `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email || id;
        return {
            id,
            authUid: row.authUid || row.uid || row.id || null,
            role,
            label,
            email: row.email || null,
            fcmToken: row.fcmToken || null,
        };
    }).filter((row) => !!row.id);
}

function dedupeByUid(rows) {
    const seen = new Set();
    return (rows || []).filter((row) => {
        const uid = normalizeRecipientUid(row);
        if (!uid || seen.has(uid)) return false;
        seen.add(uid);
        return true;
    });
}

async function fetchAudienceUsersForNotification(tenantId) {
    const dbInstance = db();
    const [studentsSnap, teachersSnap, staffSnap] = await Promise.all([
        getDocs(collection(dbInstance, 'Students', tenantId, 'students')),
        getDocs(collection(dbInstance, 'Teachers', tenantId, 'teachers')),
        getDocs(collection(dbInstance, 'staff', tenantId, 'staff')),
    ]);

    const mapDoc = (snap) => ({ id: snap.id, ...snap.data() });
    const students = normalizeAudienceRows(studentsSnap.docs.map(mapDoc), 'student');
    const teachers = normalizeAudienceRows(teachersSnap.docs.map(mapDoc).filter(isDirectoryTeacher), 'teacher');
    const staff = normalizeAudienceRows(staffSnap.docs.map(mapDoc), 'staff');
    return [...students, ...teachers, ...staff];
}

function buildRecipientsFromAudiences(audiences, users) {
    if (!Array.isArray(audiences) || !audiences.length) return [];
    const byId = new Map((users || []).map((row) => [row.id, row]));
    const recipients = [];

    audiences.forEach((entry) => {
        if (entry?.type === 'all') {
            recipients.push(...users);
            return;
        }
        if (entry?.type === 'all_teacher') {
            recipients.push(...users.filter((row) => row.role === 'teacher'));
            return;
        }
        if (entry?.type === 'all_staff') {
            recipients.push(...users.filter((row) => row.role === 'staff'));
            return;
        }
        if (entry?.type === 'all_student') {
            recipients.push(...users.filter((row) => row.role === 'student'));
            return;
        }
        if (entry?.type?.startsWith('selected_')) {
            (entry.userIds || []).forEach((id) => {
                const row = byId.get(id);
                if (row) recipients.push(row);
            });
        }
    });

    return dedupeByUid(recipients);
}

export async function createNoticeNotification({ tenantId, noticeId, noticePayload, createdBy }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!noticeId) throw new Error('Missing noticeId');
    const allAudienceUsers = await fetchAudienceUsersForNotification(tenantId);
    const recipients = buildRecipientsFromAudiences(noticePayload?.audiences || [], allAudienceUsers);
    const creatorUid = createdBy?.uid || null;
    const filteredRecipients = recipients.filter((row) => normalizeRecipientUid(row) !== creatorUid);
    if (!filteredRecipients.length) {
        return { notificationId: null, recipientCount: 0, pushed: false };
    }

    const recipientUids = filteredRecipients.map((row) => normalizeRecipientUid(row)).filter(Boolean);
    const recipientTokens = Array.from(new Set(filteredRecipients.map((row) => row.fcmToken).filter(Boolean)));
    const normalizedType = noticePayload?.type === 'notice' ? 'notice' : 'announcement';
    const baseDoc = {
        tenantId,
        type: normalizedType,
        noticeId,
        title: noticePayload?.title || 'New Notice',
        body: noticePayload?.body || '',
        communicationType: normalizedType,
        recipientIds: recipientUids,
        recipientCount: recipientUids.length,
        readBy: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: createdBy || null,
    };

    const createdItems = await Promise.all(
        filteredRecipients.map(async (recipient) => {
            const recipientUid = normalizeRecipientUid(recipient);
            if (!recipientUid) return null;
            const ref = noticeUserItemsRef(tenantId, normalizedType, recipientUid);
            const created = await addDoc(ref, {
                ...baseDoc,
                recipientIds: [recipientUid],
                recipientCount: 1,
            });
            return { recipientUid, itemId: created.id };
        })
    );
    const notificationId = createdItems.find(Boolean)?.itemId || null;
    await refreshNotificationSummary(tenantId);

    if (!recipientTokens.length) {
        return { notificationId, recipientCount: recipientUids.length, pushed: false };
    }

    const backendUrl = ENV.NOTICE_PUSH_BACKEND?.SEND_PUSH_URL;
    if (backendUrl) {
        const res = await fetch(backendUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(ENV.NOTICE_PUSH_BACKEND?.API_KEY ? { 'X-API-Key': ENV.NOTICE_PUSH_BACKEND.API_KEY } : {}),
            },
            body: JSON.stringify({
                tenantId,
                notificationId,
                title: baseDoc.title,
                body: baseDoc.body,
                type: baseDoc.type,
                noticeId,
                recipientUids,
                tokens: recipientTokens,
            }),
        });
        if (!res.ok) {
            const txt = await res.text();
            throw new Error(txt || `Push backend failed (HTTP ${res.status})`);
        }
        return { notificationId, recipientCount: recipientUids.length, pushed: true };
    }

    const fnName = ENV.FIREBASE?.SEND_NOTICE_PUSH_FUNCTION_NAME;
    if (fnName) {
        const callable = httpsCallable(getFunctions(getFirebaseApp()), fnName);
        await callable({
            tenantId,
            notificationId,
            title: baseDoc.title,
            body: baseDoc.body,
            noticeId,
            recipientUids,
            tokens: recipientTokens,
        });
        return { notificationId, recipientCount: recipientUids.length, pushed: true };
    }

    return { notificationId, recipientCount: recipientUids.length, pushed: false };
}

export async function sendStudentAttendanceNotification({
    tenantId,
    student,
    dateKey,
    status,
    reason,
    markedBy,
}) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!student) throw new Error('Missing student');
    if (!dateKey) throw new Error('Missing dateKey');
    if (status !== 'P' && status !== 'LV') return { skipped: true };

    const studentUid = student?.authUid || student?.uid || student?.id || null;
    if (!studentUid) return { skipped: true };

    const studentName = student?.studentName
        || `${student?.firstName || ''} ${student?.lastName || ''}`.trim()
        || student?.name
        || 'Student';
    const markerName = markedBy?.name || markedBy?.email || 'School';
    const title = status === 'P' ? 'Attendance Marked: Present' : 'Attendance Marked: Leave';
    const body = status === 'P'
        ? `${studentName}, your attendance for ${dateKey} is marked Present by ${markerName}.`
        : `${studentName}, your attendance for ${dateKey} is marked Leave by ${markerName}.${reason ? ` Reason: ${reason}` : ''}`;

    const notificationDoc = {
        tenantId,
        type: 'attandance',
        communicationType: 'attandance',
        attandanceStatus: status,
        attandanceDateKey: dateKey,
        studentId: studentUid,
        title,
        body,
        recipientIds: [studentUid],
        recipientCount: 1,
        readBy: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: markedBy || null,
    };
    const created = await addDoc(attandanceUserItemsRef(tenantId, studentUid), notificationDoc);
    await refreshNotificationSummary(tenantId);

    const recipientToken = student?.fcmToken || null;
    if (!recipientToken) {
        return { notificationId: created.id, recipientCount: 1, pushed: false };
    }

    const backendUrl = ENV.NOTICE_PUSH_BACKEND?.SEND_PUSH_URL;
    if (backendUrl) {
        const res = await fetch(backendUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(ENV.NOTICE_PUSH_BACKEND?.API_KEY ? { 'X-API-Key': ENV.NOTICE_PUSH_BACKEND.API_KEY } : {}),
            },
            body: JSON.stringify({
                tenantId,
                notificationId: created.id,
                title,
                body,
                type: 'attandance',
                recipientUids: [studentUid],
                tokens: [recipientToken],
            }),
        });
        if (!res.ok) {
            const txt = await res.text();
            throw new Error(txt || `Push backend failed (HTTP ${res.status})`);
        }
        return { notificationId: created.id, recipientCount: 1, pushed: true };
    }

    const fnName = ENV.FIREBASE?.SEND_NOTICE_PUSH_FUNCTION_NAME;
    if (fnName) {
        const callable = httpsCallable(getFunctions(getFirebaseApp()), fnName);
        await callable({
            tenantId,
            notificationId: created.id,
            title,
            body,
            recipientUids: [studentUid],
            tokens: [recipientToken],
            type: 'attandance',
        });
        return { notificationId: created.id, recipientCount: 1, pushed: true };
    }

    return { notificationId: created.id, recipientCount: 1, pushed: false };
}

export function subscribeTenantNotifications({ tenantId, userUid, onNext, onError, pageSize = 20 }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!userUid) throw new Error('Missing userUid');
    const noticeQ = query(noticeUserItemsRef(tenantId, 'notice', userUid), orderBy('createdAt', 'desc'));
    const announcementQ = query(noticeUserItemsRef(tenantId, 'announcement', userUid), orderBy('createdAt', 'desc'));
    const attendanceQ = query(attandanceUserItemsRef(tenantId, userUid), orderBy('createdAt', 'desc'));

    let noticeRows = [];
    let announcementRows = [];
    let attendanceRows = [];

    const decorate = (bucket, row) => ({
        ...row,
        id: `${bucket}:${userUid}:${row.id}`,
        _bucket: bucket,
        _ownerUid: userUid,
        _rawId: row.id,
    });

    const emit = () => {
        const merged = [
            ...noticeRows.map((r) => decorate('notice', r)),
            ...announcementRows.map((r) => decorate('announcement', r)),
            ...attendanceRows.map((r) => decorate('attandance', r)),
        ]
            .sort((a, b) => {
                const am = typeof a?.createdAt?.toDate === 'function' ? a.createdAt.toDate().getTime() : 0;
                const bm = typeof b?.createdAt?.toDate === 'function' ? b.createdAt.toDate().getTime() : 0;
                return bm - am;
            })
            .slice(0, pageSize);
        onNext?.(merged);
    };

    const unsubNotice = onSnapshot(
        noticeQ,
        (snap) => {
            noticeRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            emit();
        },
        (err) => onError?.(err)
    );

    const unsubAnnouncement = onSnapshot(
        announcementQ,
        (snap) => {
            announcementRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            emit();
        },
        (err) => onError?.(err)
    );

    const unsubAttendance = onSnapshot(
        attendanceQ,
        (snap) => {
            attendanceRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            emit();
        },
        (err) => onError?.(err)
    );

    return () => {
        unsubNotice?.();
        unsubAnnouncement?.();
        unsubAttendance?.();
    };
}

export async function markNotificationAsRead({ tenantId, notificationId, userUid }) {
    if (!tenantId || !notificationId || !userUid) return;
    const [bucket, ownerUid, rawId] = String(notificationId).split(':');
    if (!bucket || !ownerUid || !rawId) return;
    const ref = doc(db(), COLLECTIONS.NOTIFICATIONS, tenantId, bucket, ownerUid, 'items', rawId);
    await updateDoc(ref, {
        readBy: arrayUnion(userUid),
        updatedAt: serverTimestamp(),
    });
    await refreshNotificationSummary(tenantId);
}

export async function markAllNotificationsAsRead({ tenantId, notificationIds, userUid }) {
    if (!tenantId || !userUid || !Array.isArray(notificationIds) || !notificationIds.length) return;
    await Promise.all(notificationIds.map((notificationId) => markNotificationAsRead({
        tenantId,
        notificationId,
        userUid,
    })));
}

export async function seedNotificationReadState({ tenantId, notificationId, userUid }) {
    if (!tenantId || !notificationId || !userUid) return;
    const [bucket, ownerUid, rawId] = String(notificationId).split(':');
    if (!bucket || !ownerUid || !rawId) return;
    const ref = doc(db(), COLLECTIONS.NOTIFICATIONS, tenantId, bucket, ownerUid, 'items', rawId);
    await setDoc(ref, { readBy: arrayUnion(userUid), updatedAt: serverTimestamp() }, { merge: true });
    await refreshNotificationSummary(tenantId);
}
